/**
 * SettingsLegalPage
 * Lists legal documents accessible from the Settings nav.
 * On desktop these are also in the app footer; on mobile this is the only entry point.
 * Route: /settings/legal
 */

import { useState } from 'react';
import { openCookiePreferences } from '@resida/analytics';
import { LANDLORD_POLICY_LINKS } from '@resida/consent-ui';
import type { DocumentPolicyType } from '@resida/shared';
import { PageHeader } from '../components/layout/index';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { PolicyModal } from '../components/PolicyModal';

const ROW_CLASS =
  'flex items-center justify-between py-4 min-h-[44px] text-left w-full hover:bg-accent/50 transition-colors -mx-[var(--page-padding-x)] px-[var(--page-padding-x)]';

export function SettingsLegalPage() {
  const [openPolicy, setOpenPolicy] = useState<DocumentPolicyType | null>(null);

  return (
    <SettingsLayout>
      <div data-testid="settings-legal-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader title="Legal" subtitle="Review our policies and terms." />
        </div>

        <div className="px-[var(--page-padding-x)] flex flex-col divide-y divide-border border-y border-border">
          {LANDLORD_POLICY_LINKS.map(({ key, label, description }) => (
            <button
              key={key}
              type="button"
              onClick={() => setOpenPolicy(key)}
              className={ROW_CLASS}
              data-testid={`legal-link-${key}`}
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">{label}</span>
                <span className="text-xs text-muted-foreground">{description}</span>
              </div>
              <span className="text-muted-foreground ml-4 shrink-0">›</span>
            </button>
          ))}
          <button
            type="button"
            onClick={openCookiePreferences}
            className={ROW_CLASS}
            data-testid="legal-link-cookie-preferences"
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">Cookie preferences</span>
              <span className="text-xs text-muted-foreground">Choose whether Resida may use analytics cookies in this browser.</span>
            </div>
            <span className="text-muted-foreground ml-4 shrink-0">›</span>
          </button>
        </div>

        {openPolicy && (
          <PolicyModal
            policyType={openPolicy}
            isOpen={true}
            onClose={() => setOpenPolicy(null)}
          />
        )}
      </div>
    </SettingsLayout>
  );
}

export default SettingsLegalPage;
